import { useState } from "react";
import { Button, Dropdown, Flex, MenuProps, Popconfirm } from "antd";
import {
  DotsThreeVerticalIcon,
  NotePencilIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import {
  CanAccess,
  useDelete,
  useInvalidate,
  useTranslate,
} from "@refinedev/core";
import { DeleteButtonWithErrorPopover } from "./DeleteButtonWithErrorPopover";
import { useAntTheme } from "hooks/useAntTheme/";

export const MenuCTA = ({
  onEditClick,
  recordId,
  resource = "media/projects",
  onDeleteSuccess,
  invalidationResource,
  extraItems,
}: {
  onEditClick: () => void;
  recordId: string;
  resource?: string;
  onDeleteSuccess?: () => void | Promise<unknown>;
  invalidationResource?: string;
  extraItems?: MenuProps["items"];
}) => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { theme } = useAntTheme();
  const [open, setOpen] = useState(false);
  const { mutateAsync: deleteRecord, isLoading: isDeleting } = useDelete();
  const buttonStyle: React.CSSProperties = {
    background: "transparent",
    justifyContent: "flex-start",
  };

  const handleDelete = async () => {
    try {
      await deleteRecord({
        resource,
        id: recordId,
        // Skip default `resourceAll` invalidation so per-record `useOne`
        // queries (e.g. RecentProjectCard) don't refetch the just-deleted
        // id and 404. The parent's `onDeleteSuccess` invalidates the list.
        invalidates: ["list", "many"],
        successNotification: {
          type: "success",
          message: t("notifications.deleteSuccessGeneric"),
        },
        errorNotification: false,
      });
    } finally {
      setOpen(false);
      await onDeleteSuccess?.();
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Button
          size="small"
          type="text"
          icon={<NotePencilIcon />}
          style={buttonStyle}
        >
          {t("actions.edit")}
        </Button>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
        setOpen(false);
        onEditClick();
      },
    },
    ...(extraItems ?? []),
    {
      key: "2",
      label: (
        <CanAccess resource="media_projects" action="delete">
          {onDeleteSuccess ? (
            <Popconfirm
              title={t("buttons.confirm")}
              onConfirm={() => {
                handleDelete().catch((error) => {
                  console.error("Failed to delete project", error);
                });
              }}
              onCancel={() => setOpen(false)}
              okText={t("buttons.delete")}
              cancelText={t("buttons.cancel")}
              okButtonProps={{ danger: true, loading: isDeleting }}
            >
              <Flex
                align="center"
                gap={8}
                style={{ width: "100%" }}
                onClick={(e) => e.stopPropagation()}
              >
                <TrashIcon />
                <span>{t("actions.delete")}</span>
              </Flex>
            </Popconfirm>
          ) : (
            <DeleteButtonWithErrorPopover
              size="small"
              type="text"
              resource={resource}
              recordItemId={recordId}
              accessControl={{ enabled: false }}
              color="danger"
              style={{ ...buttonStyle, width: "100%" }}
              invalidates={["list", "many"]}
              onSuccess={() => {
                invalidate({
                  resource: invalidationResource ?? resource,
                  invalidates: ["list"],
                }).catch((error) => {
                  console.error("Failed to invalidate projects list", error);
                });

                setOpen(false);
              }}
            />
          )}
        </CanAccess>
      ),
      danger: !!onDeleteSuccess,
    },
  ];
  return (
    <Dropdown
      menu={{
        items,
      }}
      onOpenChange={setOpen}
      placement="bottomRight"
      trigger={["click"]}
      open={open}
    >
      <Button
        icon={
          <DotsThreeVerticalIcon
            size={20}
            weight="bold"
            color={theme.colorBlack}
          />
        }
        type="text"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
      />
    </Dropdown>
  );
};
